import { axiosInstance } from "@src/api/axiosConfig";
import { nestPath } from "../landRegister/axoisPath";
import { useMutation, useQuery, useInfiniteQuery } from "react-query";

const path = {
    messageList: `${nestPath}/chatting/message/list`,
    messageSend: `${nestPath}/chatting/message`, // 채팅 메시지 전송
    messageRead: `${nestPath}/chatting/message/read`, // 채팅 메시지 읽음 처리
    messageReadSendbird: `${nestPath}/chatting/message/read/sendbird`, // 채팅 메시지 읽음 처리
    chattingRoom: (chattingRoomNo: number) => `${nestPath}/chatting/room/${chattingRoomNo}`, // 현재 채팅방 정보 조회
    activateChattingRoom: `${nestPath}/chatting/room/activate`, // 채팅방 활성화
};

// 채팅 메시지 리스트 조회 -----
const getMessageList = async (params: GetMessageListParams) => {
    const response = await axiosInstance.get<GetMessageListResponseDto>(path.messageList, { params });
    return response.data;
};

export const useGetMessageList = (params: GetMessageListParams) => {
    return useInfiniteQuery({
        queryKey: ["msgMessages", params.chattingRoomNo],
        queryFn: ({ pageParam = "" }) => getMessageList({ ...params, cursor: pageParam }),
        getNextPageParam: (lastPage) => (lastPage?.cursor ? lastPage.cursor : undefined),
        select: (data) => ({
            ...data,
            pages: data.pages.flatMap((page) => page.list),
        }),
    });
};
// 채팅 메시지 전송 -----
const sendMessage = async (body: SendMessageRequestBody) => {
    const response = await axiosInstance.post(path.messageSend, body);
    return response.data;
};

export const useSendMessage = () => {
    return useMutation({ mutationFn: (body: SendMessageRequestBody) => sendMessage(body) });
};

// 채팅 메시지 읽음 처리 -----
const readMsgMessage = async (body: ReadMessageRequestBody) => {
    const response = await axiosInstance.post(path.messageRead, body);
    return response.data;
};

export const useReadMsgMessage = () => {
    return useMutation({ mutationFn: (body: ReadMessageRequestBody) => readMsgMessage(body) });
};

const readSendbirdMessage = async (body: ReadSendbirdMessageRequestBody) => {
    const response = await axiosInstance.post(path.messageReadSendbird, body);
    return response.data;
};

export const useReadSendbirdMessage = () => {
    return useMutation({ mutationFn: (body: ReadSendbirdMessageRequestBody) => readSendbirdMessage(body) });
};
// 채팅방 정보 조회 -----
const getChattingRoomInfo = async (chattingRoomNo: number) => {
    const response = await axiosInstance.get<GetChattingRoomInfoResponseDto>(path.chattingRoom(chattingRoomNo));
    return response.data;
};

export const useGetChattingRoomInfo = (chattingRoomNo: number, enabled: boolean) => {
    return useQuery({
        queryKey: ["chattingRoomInfo", chattingRoomNo],
        queryFn: () => getChattingRoomInfo(chattingRoomNo),
        enabled,
    });
};

// 채팅방 활성화 ----- (의뢰인이 첫 채팅 )
const activateChattingRoom = async (chattingRoomNo: number) => {
    const response = await axiosInstance.put(path.activateChattingRoom + `?chattingRoomNo=${chattingRoomNo}`);
    return response.data;
};

export const useActivateChattingRoom = () => {
    return useMutation({ mutationFn: (chattingRoomNo: number) => activateChattingRoom(chattingRoomNo) });
};

// type 정의 -----
type GetMessageListParams = {
    chattingRoomNo: number;
    cursor?: string;
    limit?: number;
};

type GetMessageListResponseDto = {
    list: MessageItem[];
    cursor: string;
};

export type MessageItem = {
    no: number;
    message: string;
    createdAt: string;
    senderNo: number;
    sendbirdMessageId: string;
    isMine: boolean;
    sendDate: string;
    isRead: boolean;
};

type SendMessageRequestBody = {
    chattingRoomNo: number;
    message: string;
};

type ReadMessageRequestBody = {
    chattingRoomNo: number;
    messageNo: number;
};

type ReadSendbirdMessageRequestBody = {
    chattingRoomNo: number;
    sendbirdMessageId: string;
};

export type ChattingRoomItem = {
    matchNo: number;
    status: number;
    itemImage: string;
    itemAddress: string;
    itemUseDetail: string;
    itemId: string;
    itemSubId: string;
    bidType: number;
    isNew: boolean;
    matchRequests: {
        matchRequestNo: number;
        profileImageUrl: string;
    }[];
};

export type NewChattingRoomItem = {
    isNewMessage: false;
    isNew: false;
    matchNo: number;
    completeRequestStatus: CompleteRequestStatus;
    status: number;
    serviceType: number;
    serviceTypeKor: string;
    itemImage: string;
    itemAddress: string;
    itemUseDetail: string;
    itemId: string;
    itemSubId: string;
    bidType: number;
    isExistReview: boolean;
    isCompleteConsulting: boolean;
};

export type OtherUser = {
    userNo: number;
    name: string;
    phone: string;
    profileImage: string;
};

// 1: 매치신청, 2: 진행중, 3: 시간만료되어 취소, 4: 마감, 200: 완료
export enum MatchStatusType {
    "매치신청" = 1,
    "진행중" = 2,
    "시간만료취소" = 3,
    "마감" = 4,
    "완료" = 200,
}

export enum MatchUserStatusType {
    "요청중" = MatchStatusType.매치신청,
    "진행중" = MatchStatusType.진행중,
    "완료" = MatchStatusType.완료,
    "취소" = MatchStatusType.마감,
}

export enum MatchTakerStatusType {
    "지원중" = MatchStatusType.매치신청,
    "수행중" = MatchStatusType.진행중,
    "완료" = MatchStatusType.완료,
    "지원취소" = MatchStatusType.마감,
}

type GetChattingRoomInfoResponseDto = {
    chattingRoomNo: number;
    matchRequestNo: number;
    matchStatus: MatchStatusType;
    item: NewChattingRoomItem;
    otherUser: OtherUser;
};

export enum CompleteRequestStatus {
    "진행중" = 0,
    "완료대기" = 1,
    "완료승인" = 2,
}
